import React, { useState, useEffect, useLayoutEffect } from "react";
import Layout from "Components/Layout";
import BlogClasses from "./index.module.css";
import { Helmet } from "react-helmet";
import { useForm, Controller } from "react-hook-form";
import { FileUploader } from "react-drag-drop-files";
import { BsCloudUpload } from "react-icons/bs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { Services } from "Services";
import { SuccessSwal, warningSwal } from "Util/Toast";
import ReactLoading from "react-loading";
import { NullCheck } from "Components/validators";
import MainLoader from "Components/loader/loader";
window.Buffer = window.Buffer || require("buffer").Buffer;

const BLOG_CATEGORIES = [
    "learning",
    "blogs",
    "Reading & Literacy",
    "Reading / Phonics",
    "Phonics",
    "Parenting",
    "Parenting & Technology",
];

// Based on the last several posts' hero images -- guidance only, not enforced.
const RECOMMENDED_IMAGE_SIZE = "~1200-1600 x 630-900px (approx. 16:9)";

const readImageDimensions = (src) =>
    new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () =>
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        img.onerror = reject;
        img.src = src;
    });

function AddBlog() {
    const [loading, setLoading] = useState(false);
    const [imageUploading, setImageUploading] = useState(false);
    const [searchParams] = useSearchParams();
    const [edit, setEdit] = useState(false);
    const [imagePreview, setImagePreview] = useState("");
    const [mainLoad, setMainLoad] = useState(false);
    const Navigate = useNavigate();
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        control,
        formState: { errors },
    } = useForm({ defaultValues: { Status: "draft", Content: "" } });
    const token = { Authorization: `token ${localStorage.getItem("token")}` };

    const contentImageUploadHandler = (blobInfo) =>
        new Promise((resolve, reject) => {
            const namedFile = new File(
                [blobInfo.blob()],
                blobInfo.filename() || `${Date.now()}.png`,
                { type: blobInfo.blob().type }
            );
            Services.uploadFile(namedFile, "Blogs", token)
                .then((res) => {
                    if (res.fileUrl) {
                        resolve(res.fileUrl);
                    } else {
                        reject(res.Message || "image upload failed");
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject("image upload failed, please try again");
                });
        });

    useLayoutEffect(() => {
        if (searchParams.get("id")) {
            setEdit(true);
            setMainLoad(true);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (searchParams.get("id")) {
            Services.getBlogPostById(
                "GET",
                null,
                token,
                searchParams.get("id")
            )
                .then((response) => {
                    setTimeout(() => {
                        setMainLoad(false);
                    }, 200);
                    if (response.Status === 1) {
                        const post = response.BlogPost;
                        reset({
                            Slug: post.Slug,
                            Heading: post.Heading,
                            SubHeading: post.SubHeading,
                            Category: post.Category,
                            Content: post.Content,
                            ImageAlt: post.ImageAlt,
                            MetaTitle: post.MetaTitle,
                            MetaDescription: post.MetaDescription,
                            OgTitle: post.OgTitle,
                            OgDescription: post.OgDescription,
                            FaqSchema: post.FaqSchema
                                ? JSON.stringify(post.FaqSchema, null, 2)
                                : "",
                            Status: post.Status || "draft",
                            PostedOn: post.PostedOn
                                ? String(post.PostedOn).slice(0, 10)
                                : "",
                        });
                        setValue("ImageUrl", post.ImageUrl || "");
                        setImagePreview(post.ImageUrl || "");
                        if (post.ImageUrl) {
                            readImageDimensions(post.ImageUrl)
                                .then(({ width, height }) => {
                                    setValue("ImageWidth", width);
                                    setValue("ImageHeight", height);
                                })
                                .catch(() => {});
                        }
                    } else if (response.Status === 0) {
                        warningSwal("Warning", response.Message);
                    }
                })
                .catch((err) => {
                    setMainLoad(false);
                    alert("something went wrong please try again");
                    console.log(err);
                });
        }
        // eslint-disable-next-line
    }, []);

    function imageHandleChange(file) {
        if (!file.type || !file.type.startsWith("image/")) {
            alert("please upload an image file");
            return false;
        }
        const localUrl = URL.createObjectURL(file);
        readImageDimensions(localUrl)
            .then(({ width, height }) => {
                setValue("ImageWidth", width);
                setValue("ImageHeight", height);
            })
            .catch(() => {})
            .finally(() => URL.revokeObjectURL(localUrl));

        setImageUploading(true);
        Services.uploadFile(file, "Blogs", token)
            .then((res) => {
                setImageUploading(false);
                if (res.fileUrl) {
                    setImagePreview(res.fileUrl);
                    setValue("ImageUrl", res.fileUrl);
                } else {
                    alert(res.Message || "image upload failed");
                }
            })
            .catch((err) => {
                setImageUploading(false);
                alert("image upload failed, please try again");
                console.log(err);
            });
        return true;
    }

    const onSubmit = (data) => {
        let faqSchema;
        if (data.FaqSchema && data.FaqSchema.trim()) {
            try {
                faqSchema = JSON.parse(data.FaqSchema);
            } catch (e) {
                alert("FAQ Schema must be valid JSON");
                return;
            }
        }

        const payload = {
            Slug: data.Slug,
            Heading: data.Heading,
            SubHeading: data.SubHeading,
            Category: data.Category,
            Content: data.Content,
            ImageUrl: data.ImageUrl,
            ImageAlt: data.ImageAlt,
            // Not part of the documented API schema yet -- harmless extra
            // keys until the backend adds matching columns to persist them.
            ImageWidth: data.ImageWidth ? Number(data.ImageWidth) : undefined,
            ImageHeight: data.ImageHeight
                ? Number(data.ImageHeight)
                : undefined,
            MetaTitle: data.MetaTitle,
            MetaDescription: data.MetaDescription,
            OgTitle: data.OgTitle,
            OgDescription: data.OgDescription,
            Status: data.Status,
            PostedOn: data.PostedOn || undefined,
            ...(faqSchema !== undefined ? { FaqSchema: faqSchema } : {}),
        };

        setLoading(true);
        if (edit) {
            Services.updateBlogPost(
                "PUT",
                JSON.stringify(payload),
                token,
                searchParams.get("id")
            )
                .then((response) => {
                    if (response.Status === 1) {
                        Navigate(-1);
                        SuccessSwal("Success", response.Message);
                    } else {
                        setLoading(false);
                        warningSwal("Warning", response.Message);
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    alert("something went wrong please try again");
                    console.log(err);
                });
        } else {
            Services.addBlogPost("POST", JSON.stringify(payload), token)
                .then((response) => {
                    if (response.Status === 1) {
                        Navigate(-1);
                        SuccessSwal("Success", response.Message);
                    } else {
                        setLoading(false);
                        warningSwal("Warning", response.Message);
                    }
                })
                .catch((err) => {
                    setLoading(false);
                    alert("something went wrong please try again");
                    console.log(err);
                });
        }
    };

    const fileuploadDesign = (
        <div className={BlogClasses["add-item"]}>
            <BsCloudUpload className={BlogClasses["upload-icon"]} />
            <h5>Drag Your Image here</h5>
            <p>(JPG, PNG or WEBP)</p>
        </div>
    );

    const backButton = () => {
        Navigate(-1);
    };

    return (
        <Layout Active={"Blogs"}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>{`${edit ? "Update" : "Add"} Blog`}</title>
            </Helmet>
            {mainLoad && <MainLoader />}
            <div className={BlogClasses["Container"]}>
                <button onClick={backButton} className={BlogClasses["back"]}>
                    Back
                </button>
                <div className={BlogClasses["wrapper"]}>
                    <h3>{!edit ? "Add Blog" : "Update Blog"}</h3>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="Slug">Slug</label>
                            <input
                                type="text"
                                placeholder="how-to-improve-reading-skills"
                                {...register("Slug", {
                                    required: true,
                                    validate: NullCheck,
                                })}
                            />
                            {errors.Slug && (
                                <span>This field is required</span>
                            )}
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="Heading">Heading</label>
                            <input
                                type="text"
                                {...register("Heading", {
                                    required: true,
                                    validate: NullCheck,
                                })}
                            />
                            {errors.Heading && (
                                <span>This field is required</span>
                            )}
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="Category">Category</label>
                            <select {...register("Category")}>
                                <option value="">Select category</option>
                                {BLOG_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="Status">Status</label>
                            <select {...register("Status")}>
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                            </select>
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="PostedOn">Posted On</label>
                            <input type="date" {...register("PostedOn")} />
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="SubHeading">Sub Heading</label>
                            <input type="text" {...register("SubHeading")} />
                        </div>

                        <div className={BlogClasses["file-upload-wrapper"]}>
                            <header>
                                <h3>Hero Image</h3>
                            </header>
                            <FileUploader
                                classes={BlogClasses["upload-image"]}
                                multiple={false}
                                handleChange={imageHandleChange}
                                name="file"
                                children={fileuploadDesign}
                            />
                        </div>
                        {imageUploading && (
                            <div className={BlogClasses["form-control-full"]}>
                                <p>Uploading image...</p>
                            </div>
                        )}
                        {imagePreview && (
                            <div className={BlogClasses["file-images-wrapper"]}>
                                <div className={BlogClasses["image"]}>
                                    <img src={imagePreview} alt="hero preview" />
                                </div>
                            </div>
                        )}
                        <input
                            type="hidden"
                            {...register("ImageUrl")}
                        />
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="ImageWidth">
                                Image Width (px)
                            </label>
                            <input
                                type="number"
                                {...register("ImageWidth")}
                            />
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="ImageHeight">
                                Image Height (px)
                            </label>
                            <input
                                type="number"
                                {...register("ImageHeight")}
                            />
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <p style={{ color: "grey", fontSize: "0.85rem" }}>
                                Auto-filled from the uploaded image, editable.
                                Recommended: {RECOMMENDED_IMAGE_SIZE} (based
                                on recent posts).
                            </p>
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="ImageAlt">Image Alt Text</label>
                            <input type="text" {...register("ImageAlt")} />
                        </div>

                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="Content">Content</label>
                            <Controller
                                name="Content"
                                control={control}
                                rules={{
                                    validate: (value) =>
                                        (value && value.trim().length > 0) ||
                                        "This field is required",
                                }}
                                render={({ field }) => (
                                    <Editor
                                        tinymceScriptSrc={
                                            process.env.PUBLIC_URL +
                                            "/tinymce/tinymce.min.js"
                                        }
                                        value={field.value}
                                        onEditorChange={field.onChange}
                                        init={{
                                            license_key: "gpl",
                                            height: 480,
                                            menubar: false,
                                            branding: false,
                                            plugins:
                                                "link image media lists table code",
                                            toolbar:
                                                "undo redo | blocks | bold italic underline | " +
                                                "bullist numlist | link image media table | code",
                                            // Existing posts rely on freeform inline styles
                                            // (e.g. styled CTA buttons) -- don't strip them.
                                            valid_elements: "*[*]",
                                            extended_valid_elements: "*[*]",
                                            images_upload_handler:
                                                contentImageUploadHandler,
                                        }}
                                    />
                                )}
                            />
                            {errors.Content && (
                                <span>{errors.Content.message}</span>
                            )}
                        </div>

                        <h3>SEO</h3>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="MetaTitle">Meta Title</label>
                            <input type="text" {...register("MetaTitle")} />
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="MetaDescription">
                                Meta Description
                            </label>
                            <input
                                type="text"
                                {...register("MetaDescription")}
                            />
                        </div>
                        <div className={BlogClasses["form-control"]}>
                            <label htmlFor="OgTitle">OG Title</label>
                            <input type="text" {...register("OgTitle")} />
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="OgDescription">
                                OG Description
                            </label>
                            <input
                                type="text"
                                {...register("OgDescription")}
                            />
                        </div>
                        <div className={BlogClasses["form-control-full"]}>
                            <label htmlFor="FaqSchema">
                                FAQ Schema (JSON, optional)
                            </label>
                            <textarea
                                rows={8}
                                placeholder='{"@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [...]}'
                                {...register("FaqSchema")}
                            />
                        </div>

                        <button disabled={loading || imageUploading}>
                            {loading ? (
                                <ReactLoading
                                    color="green"
                                    type="spokes"
                                    height={30}
                                    width={30}
                                />
                            ) : (
                                "Submit"
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </Layout>
    );
}

export default AddBlog;
